import { Autocomplete, Box, Button, ClickAwayListener, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Select, SelectChangeEvent, Stack, TextField, Tooltip, Typography } from "@mui/material";
import { amber, blue, blueGrey, green, grey, orange, red, yellow } from "@mui/material/colors";
import { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import ContactPageIcon from '@mui/icons-material/ContactPage';
import FileOpenIcon from '@mui/icons-material/FileOpen';
import EditDocumentIcon from '@mui/icons-material/EditDocument';
import GroupsIcon from '@mui/icons-material/Groups';
import LinkIcon from '@mui/icons-material/Link';
// import LinkOffIcon from '@mui/icons-material/LinkOff';

interface User {
	id: string;
	email: string;
	fullName: string;
}

interface UserWithAccess {
	id: string;
	email: string;
	fullName: string;
	access: string;
}

interface Props {
	isOpen: boolean;
	users: User[];
	ownerId: string;
	editorIds: string[];
	viewerIds: string[];
	canEditByLink: boolean;
	canViewByLink: boolean;
	onClose: () => void;
	onEditSaved: (value: UserWithAccess[], canEditByLink: boolean, canViewByLink: boolean) => void;
}

export default function EditClassAccessDialog(props: Props) {
	const { isOpen, users, ownerId, editorIds, viewerIds, canEditByLink, canViewByLink, onClose, onEditSaved } = props;
	const [usersWithAccess, setUsersWithAccess] = useState<UserWithAccess[]>([]);
	const [generalAccess, setGeneralAccess] = useState<string>("");
	const [isCopyLinkTooltipOpen, setIsCopyLinkTooltipOpen] = useState<boolean>(false);

	useEffect(() => {
		if (isOpen) {
			// init
			populateUsersWithAccess();
			getGeneralAccess();
		}
	}, [isOpen]);

	const userOptions = () => {
		return users.map((user) => ({ id: user.id, label: `${user.fullName} (${user.email})` }));
	}

	function populateUsersWithAccess() {
		const value: UserWithAccess[] = [];

		if (ownerId !== undefined && ownerId !== null) {
			const user: User | undefined = users.find((value) => value.id == ownerId);
			if (user !== undefined) {
				value.push({
					id: user.id,
					email: user.email,
					fullName: user.fullName,
					access: "owner"
				});
			}
		}

		editorIds.forEach(id => {
			const user: User | undefined = users.find((value) => value.id == id);
			if (user !== undefined) {
				value.push({
					id: user.id,
					email: user.email,
					fullName: user.fullName,
					access: "editor"
				});
			}
		});

		viewerIds.forEach(id => {
			const user: User | undefined = users.find((value) => value.id == id);
			if (user !== undefined) {
				value.push({
					id: user.id,
					email: user.email,
					fullName: user.fullName,
					access: "viewer"
				});
			}
		});

		setUsersWithAccess(value);
	}

	function getGeneralAccess() {
		if (canViewByLink) {
			setGeneralAccess("viewer");
		} else if (canEditByLink) {
			setGeneralAccess("editor");
		} else {
			setGeneralAccess("none");
		}
	}

	function removeUserWithAccess(id: string) {
		const index = usersWithAccess.findIndex((item) => item.id === id);

		if (index !== -1) {
			usersWithAccess.splice(index, 1);
			setUsersWithAccess([...usersWithAccess]);
		}
	}

	const handleOnSave = async () => {
		let canEditByLink = generalAccess === "editor";
		let canViewByLink = generalAccess === "viewer";

		onEditSaved(usersWithAccess, canEditByLink, canViewByLink);
		onClose();
	}

	function handleUserSelect(_event: any, value: { id: string; label: string } | null) {
		if (value === null) return;
		if (usersWithAccess.findIndex((item) => item.id === value.id) !== -1) return;

		const user: User | undefined = users.find((item) => item.id == value.id);

		if (user !== undefined) {
			setUsersWithAccess([...usersWithAccess, {
				id: user.id,
				email: user.email,
				fullName: user.fullName,
				access: "viewer"
			}])
		}
	}

	function handleUserAccessSelect(event: SelectChangeEvent) {
		const userId = event.target.name;
		const accessValue = event.target.value;
		const index = usersWithAccess.findIndex((item) => item.id === userId);

		if (index !== -1) {
			usersWithAccess[index].access = accessValue;
			setUsersWithAccess([...usersWithAccess]);
		}
	}

	function handleGeneralAccessSelect(event: SelectChangeEvent) {
		const accessValue = event.target.value;
		setGeneralAccess(accessValue);
	}

	async function copyLink() {
		try {
			const url = window.location.href;
			await navigator.clipboard.writeText(url);
			setIsCopyLinkTooltipOpen(true);
		} catch (err) {
			console.error('Failed to copy: ', err);
		}
	}


	return (
		<Dialog onClose={onClose} open={isOpen}>
			<DialogTitle>Edit Class Access</DialogTitle>
			<DialogContent sx={{ minWidth: 500 }}>
				{/* ---------- People with Access ---------- */}
				<Stack spacing={1}>
					<Typography variant="subtitle2">People with access</Typography>
					<Autocomplete
						options={userOptions()}
						sx={{ width: "100%", height: "50px" }}
						renderInput={(params) => <TextField {...params} />}
						onChange={handleUserSelect}
					/>
					<Stack border={"1px solid"} borderColor={blueGrey[200]} gap={0.5} borderRadius={1} padding={1} maxHeight={"306px"} sx={{ overflowY: "auto", overflowX: "hidden" }}>
						{usersWithAccess.map((user) => (
							<Stack key={user.id} direction={"row"} spacing={1} bgcolor={"white"} borderRadius={1} padding={1}>
								<Stack alignItems={"center"} justifyContent={"center"} paddingX={0.5}>
									{user.access === "owner" ?
										<ContactPageIcon fontSize="large" htmlColor={blue[400]}></ContactPageIcon>
										:
										<>
											{user.access === "editor" ?
												<EditDocumentIcon fontSize="large" htmlColor={green[400]}></EditDocumentIcon>
												:
												<FileOpenIcon fontSize="large" htmlColor={amber[400]}></FileOpenIcon>
											}
										</>
									}
								</Stack>
								<Stack flexGrow={1}>
									<Typography>{user.fullName}</Typography>
									<Typography color={grey[500]}>{user.email}</Typography>
								</Stack>
								<Stack justifyContent={"center"}>
									{user.access === "owner" ?
										<Typography color={grey[600]} mr={2}>Owner</Typography>
										:
										<Stack direction={"row"} gap={1}>
											<Select
												name={user.id}
												value={user.access}
												label="Access"
												onChange={handleUserAccessSelect}
											>
												<MenuItem value={"viewer"}>Viewer</MenuItem>
												<MenuItem value={"editor"}>Editor</MenuItem>
											</Select>
											<IconButton
												aria-label="close"
												color="inherit"
												size="small"
												onClick={() => {
													removeUserWithAccess(user.id);
												}}
											>
												<CloseIcon fontSize="inherit" />
											</IconButton>
										</Stack>
									}
								</Stack>
							</Stack>
						))}
					</Stack>
				</Stack>
				{/* ---------- General Access ---------- */}
				<Stack gap={1} mt={4}>
					<Typography variant="subtitle2">General access</Typography>
					<Stack direction={"row"} gap={2} padding={2} borderRadius={1} alignItems={"center"}
						bgcolor={generalAccess === "none" ? grey[200] : "white"}>
						<Stack
							borderRadius={100}
							bgcolor={generalAccess === "none" ? grey[100] : generalAccess === "viewer" ? amber[100] : green[100]}
							height={48}
							width={48}
							justifyContent={"center"}
							alignItems={"center"}>
							<GroupsIcon
								fontSize="large"
								htmlColor={generalAccess === "none" ? grey[500] : generalAccess === "viewer" ? amber[600] : green[500]}
							/>
						</Stack>
						<Typography flexGrow={1}>
							{generalAccess === "none" ?
								"No public access."
								:
								generalAccess === "viewer" ?
									"Anyone with the link can view."
									:
									"Anyone with the link can edit."
							}
						</Typography>
						<Stack gap={0.5} direction={"row"} alignItems={"center"}>
							<Select
								value={generalAccess}
								label="General Access"
								onChange={handleGeneralAccessSelect}>
								<MenuItem value={"none"}>None</MenuItem>
								<MenuItem value={"viewer"}>Viewer</MenuItem>
								<MenuItem value={"editor"}>Editor</MenuItem>
							</Select>
							<ClickAwayListener onClickAway={() => setIsCopyLinkTooltipOpen(false)}>
								<Tooltip
									placement="top"
									arrow
									describeChild
									onClose={() => setIsCopyLinkTooltipOpen(false)}
									open={isCopyLinkTooltipOpen}
									disableFocusListener
									disableHoverListener
									disableTouchListener
									title="Link Copied"
									slotProps={{
										popper: {
											disablePortal: true,
										},
									}}
								>
									<IconButton
										onClick={copyLink}
										disabled={generalAccess === "none"}
									>
										<LinkIcon fontSize="medium" />
									</IconButton>
								</Tooltip>
							</ClickAwayListener>
						</Stack>
					</Stack>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Cancel</Button>
				<Button onClick={handleOnSave}>Save</Button>
			</DialogActions>
		</Dialog>
	)
}